
import { Vue, Options } from "vue-class-component";
import CargoV2EditLayout from "./components/cargo-v2-edit-layout.vue";
import { CargoV2Controller } from "../../controllers/CargoV2Controller";
import {
  CargoV2Data,
  FormCargoV2,
  CargoV2DetailStt,
  BagOrSttDetail,
  ActualDimensionData,
  CalculateVolume
} from "@/domain/entities/CargoV2";
import CargoDisputeForm from "./components/cargo-dispute-form/index.vue";
import CargoV2ManualInput from "./components/manual-input/cargo-v2-manual-input.vue";
import ScanTableSection from "./components/scan-table-section.vue";
import { MainAppController } from "../../controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  CargoV2CalculateRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece,
  CALCULATE_TYPE
} from "./modules/cargo-module";
import {
  ModalMessageEntities,
  OptionsClass,
  Snackbar
} from "@/domain/entities/MainApp";
import {
  EditCargoApiRequest,
  EditCargoBagOrStt
} from "@/data/payload/api/CargoApiRequest";
import { DimensionData as DimensionPayload } from "@/domain/entities/Cargo";
import { CheckEligible, useCargoV2 } from "./composables";

const { onCheckEligible } = useCargoV2();
@Options({
  components: {
    CargoV2EditLayout,
    CargoDisputeForm,
    CargoV2ManualInput,
    ScanTableSection
  },
  beforeRouteLeave(_to: any, _from: any, next: any) {
    CargoV2Controller.setForm(new FormCargoV2());
    next();
  }
})
class CargoV2Edit extends Vue {
  detailData = new CargoV2Data();
  formData = new FormCargoV2();
  isValidVehicleNo = false;
  get form() {
    return CargoV2Controller.form;
  }
  loadingCalculation = false;
  isEditDimension = false;
  calculateType = "";
  originalDetailStt: CargoV2DetailStt[] = [];
  isSubmittingEdit = false;

  declare $refs: {
    scanTableSection: any;
  };
  get isDisabledEditBtn() {
    return (
      (this.form.bagOrStt.length &&
        (!this.form.actualTotalCargoGrossweigth ||
          !this.form.actualTotalCargoVolumeweight ||
          !this.form.actualDimension.cargoActualHeightDimension ||
          !this.form.actualDimension.cargoActualLengthDimension ||
          !this.form.actualDimension.cargoActualWidthDimension ||
          !this.form.cargoActualPiece)) ||
      this.isGrossWeightInputError ||
      this.isPiecesInputError ||
      this.isVolumeWeightError ||
      this.isUploadProofPhoto ||
      CargoV2Controller.isExceedMaxDisputeWeight
    );
  }

  get isGrossWeightInputError() {
    return CargoV2Controller.isGrossWeightInputError;
  }

  get isPiecesInputError() {
    return CargoV2Controller.isPiecesInputError;
  }

  get isVolumeWeightError() {
    return CargoV2Controller.isVolumeWeightError;
  }
  get isUploadProofPhoto() {
    const photoProof = (this.form.photoProof || []).filter(
      (p) => typeof p === "string" && p.trim() !== ""
    );
    return CargoV2Controller.isUploadProofPhoto && !photoProof.length;
  }

  mounted() {
    CargoV2Controller.setForm(new FormCargoV2());
    CargoV2Controller.setUploadProofPhoto(false);
    this.fetchDetailCargo();
  }

  get paramsId() {
    return this.$route.query.id as any;
  }
  get paramsNo() {
    return this.$route.params.id as any;
  }

  async fetchDetailCargo() {
    try {
      MainAppController.showLoading();
      this.detailData = await CargoV2Controller.fetchDetail(
        this.$route.query.id as any
      );
      this.originalDetailStt = [...(this.detailData.detailStt || [])];
      this.mapDetailToForm();
      this.calculateVolume();
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargoV2.components.common.errorGetDetailCargo"),
          () => this.fetchDetailCargo()
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  mapDetailToForm() {
    CargoV2Controller.setForm({
      cargoNo: this.detailData.cargoNumber,
      originAirportCode: this.detailData.airportOriginCityCode,
      destinationAirportCode: this.detailData.airportDestinationCityCode,
      cargoVehicleNo: this.detailData.vehiclePoliceNumber,
      driverSelected: this.detailData.driverName,
      partnerName: this.detailData.partnerName,
      partnerType: this.detailData.partnerType,
      estTotalCargoGrossweigth: this.detailData.totalEstCargoGrossWeight,
      estTotalCargoVolumeweight: this.detailData.totalEstCargoVolumeWeight,
      actualTotalCargoGrossweigth: this.detailData.totalActualCargoGrossWeight,
      actualTotalCargoVolumeweight: this.detailData
        .totalActualCargoVolumeWeight,
      totalStt: this.detailData.totalStt,
      totalPiece: this.detailData.totalPieces,
      productCargo: this.detailData.cargoProduct,
      cargoType: new OptionsClass({
        name: this.detailData.cargoType,
        value: this.detailData.cargoType
      }),
      originCityCode: this.detailData.originCityCode,
      originCountryCode: this.detailData.originCountryCode,
      destinationCityCode: new OptionsClass({
        name: this.detailData.destinationCityName,
        code: this.detailData.destinationCityCode,
        value: this.detailData.destinationCityCode,
        countryCode: this.detailData.destinationCountryCode
      }),
      isCargoEdit: true,
      departureDate: this.detailData.departureDate,
      arrivalDate: this.detailData.arrivalDate,
      cargoActualPiece: this.detailData.cargoActualPiece,
      nog: this.detailData.nog,
      cargoCommodity: new OptionsClass({
        name: this.detailData.cargoCommodity,
        value: this.detailData.cargoCommodityCode
      }),
      shc: this.detailData.shcCode,
      shcName: this.detailData.shcDescription,
      photoProof: [],
      cargoTotalChargeableWeight: this.detailData.cargoTotalChargeableWeight,
      cargoTotalCustomChargeableWeight: this.detailData
        .cargoTotalCustomChargeableWeight,
      cargoTotalEstimateChargeableWeight: this.detailData
        .cargoTotalEstimateChargeableWeight,
      isUseNewCalculation: true,
      calculateType: this.detailData.calculateType,
      isValidAwbManual: this.detailData.isValidAwbManual,
      isCargoDimensionOn: this.detailData.isCargoDimensionOn,
      actualDimension: new ActualDimensionData({
        cargoActualLengthDimension: this.detailData.actualDimension.length,
        cargoActualWidthDimension: this.detailData.actualDimension.width,
        cargoActualHeightDimension: this.detailData.actualDimension.height
      }),
      currentChargeableWeight: this.detailData.currentChargeableWeight
    });
    CargoV2Controller.setInitCalculateVolumeData(
      new CalculateVolume({
        chargeableWeight: this.detailData.currentChargeableWeight,
        calculateType: this.detailData.calculateType || CALCULATE_TYPE.INIT
      })
    );
    this.formData = CargoV2Controller.form;
    this._syncFormBagOrSttFromDetail();
  }

  onCalculateVolume(type: string) {
    this.calculateType = type;
    this.calculateVolume();
  }

  onEditCargo() {
    const { additions, bagOrSttPayload } = this._buildEditBagOrSttPayload();
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "are-you-sure",
        title: this.$t("cargoV2.components.common.confirmEditCargoTitle"),
        message: this.$t("cargoV2.components.common.confirmEditCargoMessage", {
          cargoNo: this.detailData.cargoNumber
        }),
        textSuccess: this.$t("cargoV2.components.common.save"),
        textCancel: this.$t("cargoV2.components.common.cancel"),
        onSubmit: async () => {
          MainAppController.closeMessageModal();
          await this.onProcessEdit(additions, bagOrSttPayload);
        },
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  private _buildEditBagOrSttPayload(): {
    additions: CargoV2DetailStt[];
    bagOrSttPayload: EditCargoBagOrStt[];
  } {
    const getNo = (item: any): { bagNo: string; sttNo: string } => {
      const pick = (...values: any[]) => {
        for (const v of values) {
          if (v === null || v === undefined) continue;
          const s = String(v).trim();
          if (s !== "") return s;
        }
        return "";
      };

      return {
        bagNo: pick(item?.baggingNumber, item?.bagNo, item?.bag_no),
        sttNo: pick(item?.sttNumber, item?.sttNo, item?.stt_no)
      };
    };

    const buildKey = (item: any) => {
      const { bagNo, sttNo } = getNo(item);
      if (bagNo !== "" && bagNo !== "-") return `BAG:${bagNo}`;
      if (sttNo !== "" && sttNo !== "-") return `STT:${sttNo}`;
      return "";
    };

    const normalizeDetail = (
      item: any,
      overrides?: Partial<CargoV2DetailStt>
    ): CargoV2DetailStt => {
      const { bagNo, sttNo } = getNo(item);
      return {
        ...(item as any),
        baggingNumber: bagNo || "",
        sttNumber: sttNo || "",
        ...(overrides || {})
      } as CargoV2DetailStt;
    };

    const initialList = (this.originalDetailStt || []) as CargoV2DetailStt[];
    const formList = (CargoV2Controller.form.bagOrStt as any) || [];

    const currentList: CargoV2DetailStt[] = (formList as any[]).flatMap(
      (item: any) => {
        const { bagNo } = getNo(item);
        if (bagNo !== "" && bagNo !== "-") {
          return [
            normalizeDetail(item, {
              sttNumber: "",
              isScanByBag: true
            })
          ];
        }

        const details = Array.isArray(item?.sttDetails) ? item.sttDetails : [];
        const list = details.length ? details : [item];
        return list.map((d: any) =>
          normalizeDetail(d, { baggingNumber: "", isScanByBag: false })
        );
      }
    );

    const initialKeys = initialList.map(buildKey).filter(Boolean);

    const currentMap = new Map<string, CargoV2DetailStt>();
    for (const item of currentList) {
      const key = buildKey(item);
      if (!key) continue;
      if (!currentMap.has(key)) currentMap.set(key, item);
    }

    const currentSet = new Set(currentMap.keys());

    const keepPayload: EditCargoBagOrStt[] = Array.from(
      currentMap.values()
    ).map(
      (i) =>
        new EditCargoBagOrStt(
          i.baggingNumber || "",
          i.sttNumber || "",
          String((i as any)?.action) === "add" ? "add" : "",
          !!(i as any)?.isScanByBag
        )
    );

    const removalKeys = initialKeys.filter((k) => !currentSet.has(k));
    const removePayload: EditCargoBagOrStt[] = Array.from(
      new Set(removalKeys).values()
    ).map((key) => {
      const [type, no] = key.split(":");
      if (type === "BAG") {
        return new EditCargoBagOrStt(no || "", "", "remove", true);
      }
      return new EditCargoBagOrStt("", no || "", "remove", false);
    });

    const additions = Array.from(currentMap.values()).filter(
      (i: any) => String(i?.action) === "add"
    ) as CargoV2DetailStt[];

    const sanitized = [...keepPayload, ...removePayload]
      .map((e) => {
        const item = e as any;
        if (item["bag_no"] && (!item["stt_no"] || item["stt_no"] === "")) {
          item["stt_no"] = undefined;
        }
        return e;
      })
      .filter(
        (e: any) => !!e["bag_no"] || (!!e["stt_no"] && e["stt_no"] !== "")
      );

    return { additions, bagOrSttPayload: sanitized };
  }

  private _syncFormBagOrSttFromDetail() {
    const source = (this.detailData.detailStt || []) as CargoV2DetailStt[];
    const mapped = source.map(
      (item) =>
        new BagOrSttDetail({
          bagNo: item.baggingNumber,
          bagVendorNo: item.baggingVendorNumber,
          sttNo: item.sttNumber,
          sttNoElexys: item.sttElexysNumber || "",
          sttTotalPiece: item.sttTotalPiece || item.totalPieces || 0,
          grossWeight: item.grossWeight || item.sttGrossWeight || 0,
          volumeWeight: item.volumeWeight || item.sttVolumeWeight || 0,
          chargeableWeight: item.chargeableWeight || 0,
          productType: item.productType || item.sttProductType || "",
          commodityId: 0,
          commodityName: item.commodityGroupName || item.sttCommodityName || "",
          commodityCode: item.commodityGroupCode || item.sttCommodityName || "",
          commodityGroupCode: item.commodityGroupCode || "",
          commodityGroupName: item.commodityGroupName || "",
          commodityGroupIsDg: !!item.isDangerousGoods,
          destinationCityCode:
            item.destinationCityCode ||
            item.sttDestinationCityId.toString() ||
            item.destination ||
            "",
          destinationCityName: item.destinationCityName || "",
          destinationDistrictCode: "",
          destinationDistrictName: "",
          shcCode: item.shcCode || "",
          shcDescription: item.shcName || "",
          sttPieces: item.sttPieces || [],
          commodityGroupIsQuarantine: !!item.isQuarantine,
          isDangerousGoods: !!item.isDangerousGoods,
          isQuarantine: !!item.isQuarantine,
          bagChargeableWeight: item.bagCustomChargeableWeight || 0,
          bagData: item.bagData,
          shipmentId: item.shipmentId || "",
          sttClientId: item.sttClientId
        })
    );
    CargoV2Controller.onGenerateGroupDataTable(mapped as any);
  }

  private _buildEditPayload() {
    const { bagOrSttPayload } = this._buildEditBagOrSttPayload();
    const photoProofSource =
      this.form.photoProof || this.detailData.photoProof || [];
    const photoProof = photoProofSource.filter(
      (p) => typeof p === "string" && p.trim() !== ""
    );
    const dimension = new DimensionPayload({
      length: Number(this.form.actualDimension.cargoActualLengthDimension),
      width: Number(this.form.actualDimension.cargoActualWidthDimension),
      height: Number(this.form.actualDimension.cargoActualHeightDimension)
    });
    return new EditCargoApiRequest(
      this.detailData.cargoNumber,
      Number(this.form.actualTotalCargoGrossweigth),
      Number(this.form.actualTotalCargoVolumeweight),
      Number(this.form.cargoActualPiece),
      bagOrSttPayload,
      dimension,
      undefined,
      [],
      photoProof,
      this.form.estTotalCargoGrossweigth,
      this.form.estTotalCargoVolumeweight,
      this.form.cargoTotalChargeableWeight,
      this.form.cargoTotalCustomChargeableWeight,
      this.form.cargoTotalEstimateChargeableWeight,
      this.form.isUseNewCalculation,
      this.form.cargoType.value,
      this.form.calculateType,
      this.form.isValidAwbManual,
      this.form.isCargoDimensionOn,
      this.form.isCargoPlaneManual ? "manual" : "auto",
      this.form.currentChargeableWeight,
      this.detailData.id
    );
  }

  private async onProcessEdit(
    additions: CargoV2DetailStt[],
    bagOrSttPayload: EditCargoBagOrStt[]
  ) {
    if (this.isSubmittingEdit) return;
    this.isSubmittingEdit = true;
    MainAppController.showLoading();
    try {
      if (!additions.length) {
        await this._doEditCargo();
        return;
      }

      const eligiblePayload: BagOrSttDetail[] = additions.map((i) => {
        const hasBag =
          i.baggingNumber && i.baggingNumber !== "-" && i.baggingNumber !== "";
        return hasBag
          ? new BagOrSttDetail({ bagNo: i.baggingNumber })
          : new BagOrSttDetail({ sttNo: i.sttNumber });
      });

      await new Promise<void>((resolve) => {
        onCheckEligible(
          new CheckEligible({
            bagOrStt: eligiblePayload,
            success: () => resolve()
          })
        );
      });
      await this._doEditCargo();
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargoV2.components.common.errorEditCargo"),
          () => this.onProcessEdit(additions, bagOrSttPayload)
        )
      );
    } finally {
      MainAppController.closeLoading();
      this.isSubmittingEdit = false;
    }
  }

  private async _doEditCargo() {
    await this.calculateVolume();
    const payload = this._buildEditPayload();
    await CargoV2Controller.onEditCargo(payload);
    await this.fetchDetailCargo();
    this.$router.push({
      path: `/cargo-v2/${this.detailData.cargoNumber}/info`,
      query: {
        id: this.detailData.id
      }
    });
    window.requestAnimationFrame(() => {
      const message = this.$t("cargoV2.components.common.successEditCargo", {
        cargoNo: this.detailData.cargoNumber
      });
      MainAppController.setSnackbarData(
        new Snackbar({
          type: "success",
          message: message
        })
      );
      MainAppController.setAutoClose(true);
      MainAppController.setSnackbar(true);
    });
  }

  async calculateVolume() {
    try {
      this.loadingCalculation = true;
      const minVolumePerPiece = getConfigMinimumVolumePerPiece(
        this.form.cargoType.value,
        this.form.isCargoPlaneManual ? "manual" : "auto"
      );
      const isMinimumVolumePerPiece = getConfigIsminimumVolumePerPiece(
        this.form.cargoType.value,
        this.form.isCargoPlaneManual ? "manual" : "auto"
      );
      const resp = await CargoV2Controller.calculateVolume(
        new CalculateVolumeApiRequestV2({
          calculateType: this.form.calculateType,
          calculateRequest: new CalculateVolumeRequestV2({
            cargoType: this.form.cargoType.value,
            length: this.form.actualDimension.cargoActualLengthDimension,
            width: this.form.actualDimension.cargoActualWidthDimension,
            height: this.form.actualDimension.cargoActualHeightDimension,
            chargeableWeightReweigh: this.form.cargoTotalChargeableWeight,
            currentGrossWeight: this.form.actualTotalCargoGrossweigth,
            divider: getConfigDimensionDivider(this.form.cargoType.value),
            isMinimumVolumePerPiece,
            minVolumePerPiece,
            totalPieces: this.form.cargoActualPiece,
            volumeWeightReweigh: this.form.actualTotalCargoVolumeweight,
            currentChargeableWeight: isMinimumVolumePerPiece
              ? this.form.currentChargeableWeight
              : this.form.cargoTotalChargeableWeight
          })
        })
      );

      if (!resp) return;

      this.form.actualDimension.cargoActualHeightDimension = resp.height;
      this.form.actualTotalCargoVolumeweight = resp.volumeWeight;
      await this.$nextTick();
      await this.$nextTick();
      await this.$nextTick();
      if (!this.isSubmittingEdit) {
        this.form.photoProof = [];
      }
      CargoV2Controller.setForm(this.form);
    } catch (error) {
      //
    } finally {
      this.loadingCalculation = false;
    }
  }
}
export default CargoV2Edit;
